const GOOGLE_MAPS_SCRIPT_ID = 'deroyalty-google-maps-js';
const GOOGLE_MAPS_SCRIPT_BASE = 'https://maps.googleapis.com/maps/api/js';
const HARARE_ORIGIN = { lat: -17.8292, lng: 31.0522 };

let googleMapsScriptPromise = null;

export function getGoogleMapsApiKey() {
  return (
    process.env.REACT_APP_GOOGLE_MAPS_API_KEY ||
    process.env.REACT_APP_GOOGLE_PLACES_API_KEY ||
    ''
  ).trim();
}

export function canUseGooglePlacesAutocomplete() {
  return Boolean(
    getGoogleMapsApiKey() ||
    window.google?.maps?.importLibrary ||
    window.google?.maps?.places?.AutocompleteService
  );
}

function getGoogleMapsScriptUrl(apiKey) {
  const params = new URLSearchParams({
    key: apiKey,
    libraries: 'places',
    loading: 'async',
    v: 'weekly',
  });
  return `${GOOGLE_MAPS_SCRIPT_BASE}?${params.toString()}`;
}

function stringifyPredictionPart(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  if (typeof value.toString === 'function') return value.toString();
  return String(value);
}

function extractLatLng(location) {
  if (!location) return '';
  const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
  const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
  if (Number.isFinite(lat) && Number.isFinite(lng)) {
    return `${lat},${lng}`;
  }
  return '';
}

function normalizePlaceText(value) {
  return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ');
}

function buildPlaceDescription(displayName, formattedAddress, fallback) {
  const name = stringifyPredictionPart(displayName).trim();
  const address = stringifyPredictionPart(formattedAddress).trim();
  const fallbackText = stringifyPredictionPart(fallback).trim();

  if (name && address) {
    const normalizedName = normalizePlaceText(name);
    const normalizedAddress = normalizePlaceText(address);
    if (normalizedAddress.startsWith(normalizedName) || normalizedAddress.includes(normalizedName)) {
      return address;
    }
    return `${name}, ${address}`;
  }

  return address || name || fallbackText;
}

function mapPlacePrediction(placePrediction, index) {
  const description = stringifyPredictionPart(placePrediction.text);
  const mainText = stringifyPredictionPart(placePrediction.mainText) || description;
  const secondaryText = stringifyPredictionPart(placePrediction.secondaryText);

  return {
    id: placePrediction.placeId || `${description}-${index}`,
    description,
    mainText,
    secondaryText,
    source: 'google',
    async fetchDetails() {
      if (typeof placePrediction.toPlace !== 'function') {
        return { displayName: mainText, formattedAddress: description, locationQuery: description };
      }

      const place = placePrediction.toPlace();
      if (typeof place?.fetchFields === 'function') {
        await place.fetchFields({
          fields: ['displayName', 'formattedAddress', 'location'],
        });
      }

      const displayName = stringifyPredictionPart(place?.displayName) || mainText;
      const formattedAddress = stringifyPredictionPart(place?.formattedAddress) || description;
      return {
        displayName,
        formattedAddress,
        locationQuery: extractLatLng(place?.location) || formattedAddress || displayName || description,
      };
    },
  };
}

function mapModernTextSearchPlace(place, index, query) {
  const displayName = stringifyPredictionPart(place?.displayName) || query;
  const formattedAddress = stringifyPredictionPart(place?.formattedAddress);
  const description = buildPlaceDescription(displayName, formattedAddress, query);

  return {
    id: place?.id || place?.placeId || `${description}-${index}`,
    description,
    mainText: displayName,
    secondaryText: formattedAddress || 'Google Maps result',
    source: 'google',
    async fetchDetails() {
      return {
        displayName,
        formattedAddress: formattedAddress || description,
        locationQuery: extractLatLng(place?.location) || description,
      };
    },
  };
}

function mapLegacyTextSearchPlace(place, placesLibrary, index, query) {
  const displayName = stringifyPredictionPart(place?.name) || query;
  const formattedAddress = stringifyPredictionPart(place?.formatted_address || place?.vicinity);
  const description = buildPlaceDescription(displayName, formattedAddress, query);
  const placeId = place?.place_id || `${description}-${index}`;

  return {
    id: placeId,
    description,
    mainText: displayName,
    secondaryText: formattedAddress || 'Google Maps result',
    source: 'google',
    async fetchDetails() {
      if (place?.geometry?.location) {
        return {
          displayName,
          formattedAddress: formattedAddress || description,
          locationQuery: extractLatLng(place.geometry.location) || description,
        };
      }

      if (typeof placesLibrary?.PlacesService !== 'function' || !place?.place_id) {
        return {
          displayName,
          formattedAddress: formattedAddress || description,
          locationQuery: description,
        };
      }

      const service = new placesLibrary.PlacesService(document.createElement('div'));
      const placeDetails = await new Promise((resolve, reject) => {
        service.getDetails({
          placeId,
          fields: ['name', 'formatted_address', 'geometry'],
        }, (result, status) => {
          if (status === getLegacyStatusOk(placesLibrary) && result) {
            resolve(result);
            return;
          }
          reject(new Error(`Google Places details failed with status ${status}`));
        });
      });

      const detailName = stringifyPredictionPart(placeDetails.name) || displayName;
      const detailAddress = stringifyPredictionPart(placeDetails.formatted_address) || formattedAddress || description;
      return {
        displayName: detailName,
        formattedAddress: detailAddress,
        locationQuery: extractLatLng(placeDetails.geometry?.location) || buildPlaceDescription(detailName, detailAddress, description),
      };
    },
  };
}

async function getModernTextSearchResults(placesLibrary, query) {
  if (typeof placesLibrary?.Place?.searchByText !== 'function') return [];

  let response;
  try {
    response = await placesLibrary.Place.searchByText({
      textQuery: query,
      fields: ['id', 'displayName', 'formattedAddress', 'location'],
      locationBias: HARARE_ORIGIN,
      maxResultCount: 6,
      region: 'zw',
    });
  } catch {
    return [];
  }

  const places = Array.isArray(response) ? response : response?.places || [];
  return places
    .map((place, index) => mapModernTextSearchPlace(place, index, query))
    .slice(0, 6);
}

function getLegacyTextSearchResults(placesLibrary, query) {
  if (typeof placesLibrary?.PlacesService !== 'function') return Promise.resolve([]);

  const service = new placesLibrary.PlacesService(document.createElement('div'));
  if (typeof service.findPlaceFromQuery !== 'function') return Promise.resolve([]);

  return new Promise((resolve, reject) => {
    service.findPlaceFromQuery({
      query,
      fields: ['place_id', 'name', 'formatted_address', 'geometry'],
      locationBias: HARARE_ORIGIN,
    }, (results = [], status) => {
      const zeroResultsStatus = placesLibrary?.PlacesServiceStatus?.ZERO_RESULTS || 'ZERO_RESULTS';
      if (status === getLegacyStatusOk(placesLibrary) || status === zeroResultsStatus) {
        resolve(results
          .map((place, index) => mapLegacyTextSearchPlace(place, placesLibrary, index, query))
          .slice(0, 6));
        return;
      }

      reject(new Error(`Google Places text search failed with status ${status}`));
    });
  });
}

async function getGoogleTextSearchResults(placesLibrary, query) {
  const modernResults = await getModernTextSearchResults(placesLibrary, query);
  if (modernResults.length) return modernResults;

  const legacyPlacesLibrary = placesLibrary?.PlacesService ? placesLibrary : window.google?.maps?.places;
  return getLegacyTextSearchResults(legacyPlacesLibrary, query);
}

function getLegacyStatusOk(placesLibrary) {
  return placesLibrary?.PlacesServiceStatus?.OK || 'OK';
}

function mapLegacyPlacePrediction(prediction, placesLibrary, index) {
  const description = prediction.description || '';
  const mainText = prediction.structured_formatting?.main_text || description;
  const secondaryText = prediction.structured_formatting?.secondary_text || '';
  const placeId = prediction.place_id || `${description}-${index}`;

  return {
    id: placeId,
    description,
    mainText,
    secondaryText,
    source: 'google',
    async fetchDetails() {
      if (typeof placesLibrary?.PlacesService !== 'function') {
        return { displayName: mainText, formattedAddress: description, locationQuery: description };
      }

      const service = new placesLibrary.PlacesService(document.createElement('div'));
      const place = await new Promise((resolve, reject) => {
        service.getDetails({
          placeId,
          fields: ['name', 'formatted_address', 'geometry'],
        }, (result, status) => {
          if (status === getLegacyStatusOk(placesLibrary) && result) {
            resolve(result);
            return;
          }
          reject(new Error(`Google Places details failed with status ${status}`));
        });
      });

      const displayName = stringifyPredictionPart(place.name) || mainText;
      const formattedAddress = stringifyPredictionPart(place.formatted_address) || description;
      return {
        displayName,
        formattedAddress,
        locationQuery: extractLatLng(place.geometry?.location) || formattedAddress || displayName || description,
      };
    },
  };
}

async function loadGoogleMapsScript(apiKey) {
  if (window.google?.maps?.importLibrary || window.google?.maps?.places) return;
  if (!apiKey) throw new Error('Missing Google Maps API key');
  if (process.env.NODE_ENV === 'test') throw new Error('Google Maps script loading is disabled in tests');

  if (!googleMapsScriptPromise) {
    googleMapsScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
      if (existingScript) {
        existingScript.addEventListener('load', resolve, { once: true });
        existingScript.addEventListener('error', reject, { once: true });
        return;
      }

      const script = document.createElement('script');
      script.id = GOOGLE_MAPS_SCRIPT_ID;
      script.src = getGoogleMapsScriptUrl(apiKey);
      script.async = true;
      script.defer = true;
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', () => reject(new Error('Unable to load Google Maps JavaScript API')), { once: true });
      document.head.appendChild(script);
    });
  }

  await googleMapsScriptPromise;
}

export async function loadGooglePlacesLibrary() {
  const apiKey = getGoogleMapsApiKey();
  await loadGoogleMapsScript(apiKey);

  if (window.google?.maps?.importLibrary) {
    return window.google.maps.importLibrary('places');
  }

  if (window.google?.maps?.places) {
    return window.google.maps.places;
  }

  throw new Error('Google Places library is unavailable');
}

function getLegacyPlacePredictions(placesLibrary, query) {
  if (typeof placesLibrary?.AutocompleteService !== 'function') {
    return getGoogleTextSearchResults(placesLibrary, query);
  }

  const service = new placesLibrary.AutocompleteService();
  const request = {
    input: query,
    componentRestrictions: { country: 'zw' },
    region: 'zw',
  };

  return new Promise((resolve, reject) => {
    service.getPlacePredictions(request, (predictions = [], status) => {
      const zeroResultsStatus = placesLibrary?.PlacesServiceStatus?.ZERO_RESULTS || 'ZERO_RESULTS';
      if (status === getLegacyStatusOk(placesLibrary) || status === zeroResultsStatus) {
        const autocompleteResults = predictions
          .map((prediction, index) => mapLegacyPlacePrediction(prediction, placesLibrary, index))
          .slice(0, 6);
        if (autocompleteResults.length) {
          resolve(autocompleteResults);
          return;
        }

        getGoogleTextSearchResults(placesLibrary, query)
          .then(resolve)
          .catch(() => resolve([]));
        return;
      }

      reject(new Error(`Google Places autocomplete failed with status ${status}`));
    });
  });
}

export async function getGooglePlacePredictions(input, sessionTokenRef) {
  const query = input.trim();
  if (query.length < 2) return [];

  const placesLibrary = await loadGooglePlacesLibrary();
  const { AutocompleteSessionToken, AutocompleteSuggestion } = placesLibrary;
  if (!AutocompleteSuggestion?.fetchAutocompleteSuggestions) {
    return getLegacyPlacePredictions(placesLibrary, query);
  }

  if (!sessionTokenRef.current && typeof AutocompleteSessionToken === 'function') {
    sessionTokenRef.current = new AutocompleteSessionToken();
  }

  const request = {
    input: query,
    language: 'en',
    region: 'zw',
    includedRegionCodes: ['zw'],
    origin: HARARE_ORIGIN,
  };

  if (sessionTokenRef.current) {
    request.sessionToken = sessionTokenRef.current;
  }

  const { suggestions = [] } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
  const autocompleteResults = suggestions
    .map((suggestion, index) => suggestion?.placePrediction ? mapPlacePrediction(suggestion.placePrediction, index) : null)
    .filter(Boolean)
    .slice(0, 6);
  if (autocompleteResults.length) return autocompleteResults;

  return getGoogleTextSearchResults(placesLibrary, query);
}

export function resetGooglePlaceSessionToken(sessionTokenRef) {
  sessionTokenRef.current = null;
}
